import { API_BASE_URL, authFetch } from './api';


// === Types ===

export type ContentPlatform = 'tiktok' | 'facebook' | 'instagram' | 'youtube';
export type ContentType = 'video' | 'slideshow';
export type YouTubeFormat = 'shorts' | 'video';

export interface ContentFileRef {
  uri: string;
  original_name?: string | null;
  size?: number | null;
}

export interface StartContentSessionRequest {
  pad_ids: string[];
  platform: ContentPlatform;
  content_type: ContentType;
  caption?: string;
  sound_url?: string;
  title?: string;
  description?: string;
  youtube_format?: YouTubeFormat;
  files: ContentFileRef[]; // display order
  return_home_on_finish?: boolean;
  persona?: string | null;
  brand_id?: string | null;
  llm_model?: string | null;
  comment_llm_model?: string | null;
  slack_bot_id?: string | null;
  max_steps?: number;
  disable_proactive_search?: boolean;
  capture_required?: boolean;
}

export interface PlatformGroup {
  platform: ContentPlatform;
  pad_ids: string[];
  caption?: string;
  sound_url?: string;
  title?: string;
  description?: string;
  youtube_format?: YouTubeFormat;
}

export interface StartMultiContentSessionRequest {
  groups: PlatformGroup[];
  content_type: ContentType;
  files: ContentFileRef[];
  return_home_on_finish?: boolean;
  persona?: string | null;
  brand_id?: string | null;
  llm_model?: string | null;
  comment_llm_model?: string | null;
  slack_bot_id?: string | null;
  max_steps?: number;
  disable_proactive_search?: boolean;
  capture_required?: boolean;
}

export interface StartMultiContentSessionResponse {
  run_id: string;
  group_id: string;
  group_ids: string[];
  groups: PlatformGroup[];
}

export interface ContentRunGroupStatus {
  platform: ContentPlatform;
  pad_count: number;
  caption?: string | null;
  group_id?: string | null;
}

export interface ContentRunStatus {
  run_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'stopped';
  current_index: number;
  current_group_id: string | null;
  group_ids: string[];
  groups: ContentRunGroupStatus[];
  content_type: ContentType;
  error_message: string | null;
  created_at: string | null;
  updated_at: string | null;
  completed_at: string | null;
}

export interface StartContentSessionResponse {
  group_id: string;
}

// === API Functions ===

const BASE = `${API_BASE_URL}/content`;

export async function ingestLink(
  url: string
): Promise<{ files: ContentFileRef[] }> {
  const res = await authFetch(`${BASE}/ingest-drive`, {
    method: 'POST',
    body: JSON.stringify({ url }),
  });
  return res.json();
}

// Deprecated alias kept for existing callers.
export const ingestDriveLink = ingestLink;

export async function startContentSession(
  request: StartContentSessionRequest
): Promise<StartContentSessionResponse> {
  const res = await authFetch(`${BASE}/sessions/start`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return res.json();
}

export async function startMultiContentSession(
  request: StartMultiContentSessionRequest
): Promise<StartMultiContentSessionResponse> {
  const res = await authFetch(`${BASE}/sessions/start-multi`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return res.json();
}

export async function getContentRunStatus(runId: string): Promise<ContentRunStatus> {
  const res = await authFetch(`${BASE}/runs/${runId}`);
  return res.json();
}

export async function stopContentRun(runId: string): Promise<{ success: boolean; run_id: string }> {
  const res = await authFetch(`${BASE}/runs/${runId}/stop`, {
    method: 'POST',
  });
  return res.json();
}
